import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportAPI } from '../utils/api';
import { BarChart2, Download, Factory, ShoppingCart, FileText, TrendingUp, Package, FlaskConical, Users, Warehouse, FileSpreadsheet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import toast from 'react-hot-toast';
import { localName, fmt, money, downloadBlob, STOCK_BADGE } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const PERIODS = ['daily', 'weekly', 'monthly', 'yearly', 'all', 'custom'];
const TABS = [
  { key: 'summary', icon: TrendingUp, label: 'summary', excel: 'summary' },
  { key: 'products', icon: Package, label: 'product_report', excel: 'products' },
  { key: 'raw_materials', icon: FlaskConical, label: 'raw_material_report', excel: 'raw_materials' },
  { key: 'customers', icon: Users, label: 'customer_report', excel: 'customers' },
  { key: 'inventory', icon: Warehouse, label: 'inventory_report', excel: 'inventory' },
  { key: 'sales', icon: ShoppingCart, label: 'sales', excel: 'sales' },
  { key: 'production', icon: Factory, label: 'production', excel: 'production' },
  { key: 'lading', icon: FileText, label: 'lading_bills', excel: 'lading' },
];

export default function Reports() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [tab, setTab] = useState('summary');
  const [period, setPeriod] = useState('monthly');
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [result, setResult] = useState({ tab: null, data: null });
  const data = result.tab === tab ? result.data : null;
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = () => (period === 'custom'
    ? { period: 'custom', date_from: range.date_from || undefined, date_to: range.date_to || undefined }
    : { period });

  useEffect(() => {
    if (period === 'custom' && !range.date_from && !range.date_to) return;
    setLoading(true);
    const p = params();
    const forTab = tab;
    const fn = {
      summary: () => reportAPI.summary(p), production: () => reportAPI.production(p), sales: () => reportAPI.sales(p),
      lading: () => reportAPI.ladingBills(p), products: () => reportAPI.products(p), raw_materials: () => reportAPI.rawMaterials(p),
      customers: () => reportAPI.customers(p), inventory: () => reportAPI.inventory(),
    }[tab];
    fn().then(r => setResult({ tab: forTab, data: r.data })).catch(() => toast.error(t('error'))).finally(() => setLoading(false));
  }, [tab, period, range.date_from, range.date_to]); // eslint-disable-line

  const handleExport = async (report_type) => {
    setExporting(true);
    try {
      const r = await reportAPI.exportExcel({ report_type, lang, ...params() });
      downloadBlob(r.data, `factory_${report_type}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(t('exported'));
    } catch { toast.error(t('error')); } finally { setExporting(false); }
  };

  const tooltipStyle = { background: '#0c1020', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#f1f3ff', fontSize: 12 };
  const current = TABS.find(x => x.key === tab);
  const Empty = () => <div className="empty-state" style={{ padding: '28px 0' }}><p>{t('no_data')}</p></div>;
  const Card = ({ title, children }) => (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}><span className="card-title">{title}</span></div>
      {children}
    </div>
  );
  const Stat = ({ label, value, color = '#fff', icon: Icon = BarChart2 }) => (
    <div className="stat-card">
      <div><p className="stat-label">{label}</p><p className="stat-value" style={{ color, fontSize: 26 }}>{value}</p></div>
      <div className="stat-icon" style={{ background: 'var(--accent-dim)' }}><Icon size={20} style={{ color: 'var(--accent)' }} /></div>
    </div>
  );

  return (
    <div className="space-y-5">
      <SectionHero title={t('reports')} subtitle={data?.date_from && <>{String(data.date_from)} → {String(data.date_to)}</>}>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="select" style={{ width: 140 }}>
            {PERIODS.map(p => <option key={p} value={p}>{t(p === 'all' ? 'all_time' : p === 'custom' ? 'custom_range' : p)}</option>)}
          </select>
          {period === 'custom' && <>
            <input type="date" className="input" style={{ width: 150 }} value={range.date_from} onChange={e => setRange(r => ({ ...r, date_from: e.target.value }))} />
            <input type="date" className="input" style={{ width: 150 }} value={range.date_to} onChange={e => setRange(r => ({ ...r, date_to: e.target.value }))} />
          </>}
          <button className="btn btn-secondary" onClick={() => handleExport(current.excel)} disabled={exporting}><Download size={14} /> {t('export_excel')}</button>
          <button className="btn btn-primary" onClick={() => handleExport('all')} disabled={exporting}><FileSpreadsheet size={14} /> {t('export_all')}</button>
        </div>
      </SectionHero>

      <div className="tabs">
        {TABS.map(({ key, icon: Icon, label }) => (
          <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}><Icon size={14} /> {t(label)}</button>
        ))}
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div> : data && (
        <>
          {tab === 'summary' && (
            <div className="grid-4">
              <Stat label={t('total_production')} value={fmt(data.production_batches)} icon={Factory} />
              <Stat label={t('rolls_produced')} value={fmt(data.rolls_produced)} icon={Package} />
              <Stat label={t('total_sales')} value={money(data.total_sales_amount)} color="#34d399" icon={ShoppingCart} />
              <Stat label={t('rolls_sold')} value={fmt(data.rolls_sold)} icon={ShoppingCart} />
              <Stat label={t('transactions')} value={fmt(data.sales_count)} icon={BarChart2} />
              <Stat label={t('lading_bills')} value={fmt(data.lading_bills)} icon={FileText} />
              <Stat label={t('total_debt')} value={money(data.total_debt)} color="#fbbf24" icon={Users} />
              <Stat label={t('low_stock')} value={fmt(data.low_stock_alerts)} color="#f87171" icon={FlaskConical} />
            </div>
          )}

          {tab === 'products' && (
            <div className="space-y-5">
              {data.products.length > 0 && (
                <div className="card">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.products.map(p => ({ name: localName(p, lang), [t('produced')]: p.produced_rolls, [t('sold')]: p.sold_rolls }))}>
                      <XAxis dataKey="name" tick={{ fill: 'rgba(200,210,255,0.5)', fontSize: 11 }} />
                      <YAxis tick={{ fill: 'rgba(200,210,255,0.5)', fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(99,102,241,0.08)' }} />
                      <Legend />
                      <Bar dataKey={t('produced')} fill="#6366f1" radius={[6, 6, 0, 0]} />
                      <Bar dataKey={t('sold')} fill="#10b981" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <Card title={t('product_report')}>
                {data.products.length === 0 ? <Empty /> : (
                  <div className="table-wrap"><table>
                    <thead><tr><th>{t('product')}</th><th>{t('produced')}</th><th>{t('sold')}</th><th>{t('sales_amount')}</th><th className="hide-mobile">{t('shipped')}</th><th>{t('stock')}</th><th>{t('status')}</th></tr></thead>
                    <tbody>{data.products.map(p => (
                      <tr key={p.id}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(p, lang)}</td>
                        <td>{fmt(p.produced_rolls)}</td><td>{fmt(p.sold_rolls)}</td>
                        <td style={{ color: 'var(--green)' }}>{money(p.sales_amount)}</td>
                        <td className="hide-mobile">{fmt(p.shipped_rolls)}</td>
                        <td>{fmt(p.stock_rolls)} <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({fmt(p.stock_sqm)} {t('sqm')})</span></td>
                        <td><span className={`badge ${STOCK_BADGE[p.stock_status]}`}>{t('stock_' + p.stock_status)}</span></td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </Card>
            </div>
          )}

          {tab === 'raw_materials' && (
            <Card title={t('raw_material_report')}>
              {data.materials.length === 0 ? <Empty /> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>{t('raw_material')}</th><th>{t('consumed')}</th><th className="hide-mobile">{t('batches')}</th><th>{t('stock')}</th><th className="hide-mobile">{t('low_stock_threshold')}</th><th>{t('status')}</th></tr></thead>
                  <tbody>{data.materials.map(m => (
                    <tr key={m.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(m, lang)}</td>
                      <td>{fmt(m.consumed)} {m.unit}</td>
                      <td className="hide-mobile">{m.batches}</td>
                      <td>{fmt(m.stock)} {m.unit}</td>
                      <td className="hide-mobile">{fmt(m.threshold)}</td>
                      <td><span className={`badge ${STOCK_BADGE[m.stock_status]}`}>{t('stock_' + m.stock_status)}</span></td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </Card>
          )}

          {tab === 'customers' && (
            <Card title={t('customer_report')}>
              {data.customers.length === 0 ? <Empty /> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>{t('customer')}</th><th>{t('transactions')}</th><th>{t('total_sales')}</th><th>{t('debt')}</th><th className="hide-mobile">{t('last_sale')}</th></tr></thead>
                  <tbody>{data.customers.map(c => (
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.name}</td>
                      <td>{c.sales_count}</td>
                      <td style={{ color: 'var(--green)' }}>{money(c.total_amount)}</td>
                      <td style={{ color: c.debt > 0 ? '#fbbf24' : 'var(--text-muted)', fontWeight: c.debt > 0 ? 700 : 400 }}>{money(c.debt)}</td>
                      <td className="hide-mobile">{c.last_sale || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </Card>
          )}

          {tab === 'inventory' && (
            <div className="space-y-5">
              <Card title={t('finished_goods')}>
                {data.products.length === 0 ? <Empty /> : (
                  <div className="table-wrap"><table>
                    <thead><tr><th>{t('product')}</th>{data.warehouses.map(w => <th key={w.id}>{w.name}</th>)}<th>{t('total')}</th><th>{t('status')}</th></tr></thead>
                    <tbody>{data.products.map(p => (
                      <tr key={p.id}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(p, lang)}</td>
                        {p.warehouses.map(w => <td key={w.warehouse_id}>{fmt(w.rolls)}</td>)}
                        <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(p.total_rolls)}</td>
                        <td><span className={`badge ${STOCK_BADGE[p.stock_status]}`}>{t('stock_' + p.stock_status)}</span></td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </Card>
              <Card title={t('raw_materials')}>
                {data.raw_materials.length === 0 ? <Empty /> : (
                  <div className="table-wrap"><table>
                    <thead><tr><th>{t('raw_material')}</th><th>{t('stock')}</th><th>{t('low_stock_threshold')}</th><th>{t('status')}</th></tr></thead>
                    <tbody>{data.raw_materials.map(m => (
                      <tr key={m.id}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(m, lang)}</td>
                        <td>{fmt(m.stock)} {m.unit}</td><td>{fmt(m.threshold)}</td>
                        <td><span className={`badge ${STOCK_BADGE[m.stock_status]}`}>{t('stock_' + m.stock_status)}</span></td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </Card>
            </div>
          )}

          {tab === 'sales' && (
            <div className="space-y-5">
              <div className="grid-3">
                <Stat label={t('total_sales')} value={money(data.total_amount)} color="#34d399" icon={ShoppingCart} />
                <Stat label={t('transactions')} value={fmt(data.total_transactions)} />
                <Stat label={t('rolls_sold')} value={fmt(data.total_rolls)} icon={Package} />
              </div>
              {Object.keys(data.by_payment_type || {}).length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(data.by_payment_type).map(([k, v]) => <span key={k} className="badge badge-blue" style={{ fontSize: 12, padding: '5px 12px' }}>{t('pay_' + k)}: {money(v)}</span>)}
                </div>
              )}
              <Card title={t('sales')}>
                {data.sales.length === 0 ? <Empty /> : (
                  <div className="table-wrap"><table>
                    <thead><tr><th>{t('sale_number')}</th><th>{t('date')}</th><th>{t('customer')}</th><th className="hide-mobile">{t('payment_type')}</th><th className="hide-mobile">{t('rolls')}</th><th>{t('amount')}</th></tr></thead>
                    <tbody>{data.sales.map((s, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{s.sale_number}</td>
                        <td>{s.sale_date}</td><td>{s.customer || '—'}</td>
                        <td className="hide-mobile">{t('pay_' + (s.payment_type || 'cash'))}</td>
                        <td className="hide-mobile">{fmt(s.total_rolls)}</td>
                        <td style={{ color: 'var(--green)', fontWeight: 600 }}>{money(s.total_amount)}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </Card>
            </div>
          )}

          {tab === 'production' && (
            <Card title={`${data.total_batches} ${t('batches')} · ${fmt(data.total_rolls)} ${t('rolls')}`}>
              {data.batches.length === 0 ? <Empty /> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>{t('batch_number')}</th><th>{t('date')}</th><th>{t('outputs')}</th><th className="hide-mobile">{t('inputs')}</th></tr></thead>
                  <tbody>{data.batches.map((b, i) => (
                    <tr key={i}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{b.batch_number}</td>
                      <td>{b.production_date}</td>
                      <td>{b.outputs.map(o => `${o.name}: ${fmt(o.rolls)} ${t('rolls')}`).join(' · ')}</td>
                      <td className="hide-mobile">{b.inputs.map(x => `${x.name}: ${fmt(x.quantity)} ${x.unit}`).join(' · ')}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </Card>
          )}

          {tab === 'lading' && (
            <div className="space-y-5">
              <div className="grid-2">
                <Stat label={t('lading_bills')} value={fmt(data.total_bills)} icon={FileText} />
                <Stat label={t('customs_fee')} value={money(data.total_customs_fee)} color="#fbbf24" />
              </div>
              <Card title={t('lading_bills')}>
                {data.bills.length === 0 ? <Empty /> : (
                  <div className="table-wrap"><table>
                    <thead><tr><th>{t('bill_number')}</th><th>{t('date')}</th><th>{t('customer')}</th><th className="hide-mobile">{t('destination')}</th><th>{t('customs')}</th></tr></thead>
                    <tbody>{data.bills.map((b, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{b.original_bill_number || '—'}</td>
                        <td>{b.bill_date}</td><td>{b.customer || '—'}</td>
                        <td className="hide-mobile">{b.destination || '—'}</td>
                        <td>{b.has_customs ? <span className="badge badge-orange">{b.customs_bill_number || t('yes')}</span> : <span className="badge" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>{t('no')}</span>}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
